/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import React from "react";

import { useTranslation } from "react-i18next";

import Tooltip from "@/components/Tooltip/Tooltip";
import { Typography } from "@mui/material";

import {
  CheckCircleIcon,
  colors,
  HelpIcon,
  WarningIcon
} from "@dotnet/telematics-design-system";

import { REQUIREMENT_STATUS } from "../../../types/enums";

interface RequirementsCardProps {
  icon: React.JSX.Element;
  title: string;
  status: REQUIREMENT_STATUS;
  statusDescription?: string;
}

const RequirementsCard = ({
  icon,
  status,
  title,
  statusDescription
}: RequirementsCardProps) => {
  const { t } = useTranslation();
  const statusIcons: Record<REQUIREMENT_STATUS, React.JSX.Element> = {
    [REQUIREMENT_STATUS.Error]: (
      <WarningIcon
        style={{
          color: colors.orange600
        }}
        data-testid="requirements-error-icon"
      />
    ),
    [REQUIREMENT_STATUS.Unknown]: (
      <Tooltip
        placement="top"
        size="medium"
        title={t(
          "Download monitoring##requirements##ignition unavailable tooltip"
        )}
      >
        <HelpIcon
          style={{
            color: colors.textPlaceholder
          }}
          data-testid="requirements-unknown-icon"
        />
      </Tooltip>
    ),
    [REQUIREMENT_STATUS.Success]: (
      <CheckCircleIcon
        style={{
          color: colors.green600
        }}
        data-testid="requirements-success-icon"
      />
    )
  };

  const getStatusIcon = () => statusIcons[status];

  const borderColors: Record<REQUIREMENT_STATUS, string> = {
    [REQUIREMENT_STATUS.Error]: colors.orange300,
    [REQUIREMENT_STATUS.Unknown]: colors.gray200,
    [REQUIREMENT_STATUS.Success]: colors.gray200
  };

  const backgroundColors: Record<REQUIREMENT_STATUS, string> = {
    [REQUIREMENT_STATUS.Error]: colors.orange50,
    [REQUIREMENT_STATUS.Unknown]: colors.gray100,
    [REQUIREMENT_STATUS.Success]: colors.white
  };

  const backgroundColor = backgroundColors[status];
  const borderColor = borderColors[status];

  return (
    <div
      css={css({
        border: `1px solid ${borderColor}`,
        borderRadius: "8px",
        padding: "8px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        backgroundColor,
        svg: {
          color: colors.textPlaceholder,
          width: "24px",
          height: "24px",
          flexShrink: 0,
          alignSelf: "flex-start"
        },

        span: {
          color: colors.textDark
        }
      })}
    >
      {icon}

      <div
        css={css({
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 0
        })}
      >
        <Typography variant="body3Medium">{title}</Typography>
        <Typography variant="body3Regular">{statusDescription}</Typography>
      </div>

      <div
        css={css({
          marginLeft: "auto",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          alignSelf: "flex-start"
        })}
      >
        {getStatusIcon()}
      </div>
    </div>
  );
};

export default RequirementsCard;
